import React from "react";
import {
  Calculator,
  Brain,
  Target,
  LineChart,
  BookOpen,
  Trophy,
} from "lucide-react";
import ServicePageTemplate from "../components/ServicePageTemplate";

const HERO_IMG =
  "https://images.unsplash.com/photo-1509228468518-180dd4864904?auto=format&fit=crop&w=1600&q=80";

const MathStemTutoring = () => (
  <ServicePageTemplate
    testId="math-stem-tutoring-page"
    slug="mst"
    relatedSlug="math-stem-tutoring"
    eyebrow="01 — Mathematics & STEM Tutoring"
    titleLead="Master the language of"
    titleItalic="mathematics"
    subtitle="Personalized mathematics tutoring and STEM coaching for middle school, high school, and university preparation. Building confidence, mastering difficult concepts, and developing strong mathematical thinking — not just memorizing procedures."
    heroImage={HERO_IMG}
    heroImageAlt="Chalkboard equations — mathematics as a way of thinking"
    overviewEyebrow="The Approach"
    overviewTitleLead="Think first."
    overviewTitleItalic="Then compute"
    overviewBody={[
      "Most students struggle with mathematics not because they lack ability, but because they were taught to memorize procedures instead of develop understanding. Sessions are built to reverse that.",
      "Every concept is anchored to a problem that reveals why the concept matters. Students learn to see the shape of a problem before touching a formula — the skill that separates capable mathematicians from confident test-takers.",
      "With more than two decades of classroom experience, sessions are calibrated to each student's stage, style of learning, and specific goals — whether SAT/ACT preparation, AP coursework, competition math, or university placement.",
    ]}
    whoItServes={[
      {
        title: "Middle School",
        description:
          "Pre-algebra, algebra I, geometry — building the foundation for everything ahead.",
      },
      {
        title: "High School",
        description:
          "Algebra II, geometry, precalculus, calculus (AB & BC), statistics, SAT & ACT.",
      },
      {
        title: "University Prep",
        description:
          "AP exams, subject placement tests, college calculus & linear algebra readiness.",
      },
      {
        title: "STEM Coaching",
        description:
          "Physics, engineering foundations, and technical problem-solving strategy.",
      },
    ]}
    receive={[
      "One-to-one tutoring sessions (in-person or virtual)",
      "Diagnostic assessment to identify true gaps — not surface ones",
      "Custom curriculum built around the student's schedule and goals",
      "Weekly progress notes shared with parents",
      "Homework and exam prep support between sessions",
      "Access to a small library of worked examples and practice sets",
      "Parent-teacher coordination when needed",
    ]}
    benefits={[
      {
        Icon: Brain,
        title: "Deep Understanding",
        body: "Students learn to reason mathematically — the skill that generalizes across every future course.",
      },
      {
        Icon: Trophy,
        title: "Measurable Results",
        body: "Grades and test scores improve as a natural byproduct of stronger understanding.",
      },
      {
        Icon: Target,
        title: "Exam Readiness",
        body: "Structured, time-aware prep for SAT, ACT, AP, and university placement exams.",
      },
      {
        Icon: BookOpen,
        title: "Study Skills",
        body: "How to read a math textbook. How to check your work. How to think through a proof.",
      },
      {
        Icon: LineChart,
        title: "Confidence",
        body: "The single biggest change: students stop believing they're 'bad at math.'",
      },
      {
        Icon: Calculator,
        title: "STEM Foundations",
        body: "Prepares students for engineering, physics, computer science, and quantitative fields.",
      },
    ]}
    faqs={[
      {
        q: "How are sessions delivered — in person or online?",
        a: "Both. Local students can meet in person; students outside the area meet virtually with shared digital whiteboard tools that make sessions feel just as natural.",
      },
      {
        q: "How often do students typically meet?",
        a: "Most students meet once or twice a week. Exam prep periods often step up to two or three. Frequency is calibrated to the student's goals and stage.",
      },
      {
        q: "Do you tutor SAT and ACT math specifically?",
        a: "Yes. Sessions can be entirely focused on SAT/ACT prep, or integrated with the student's coursework so both are strengthened at once.",
      },
      {
        q: "My child says they're 'just not a math person.' Can you still help?",
        a: "That belief is almost always the biggest obstacle — and it is almost always false. Most students who thought this way about themselves discover, within a few weeks, that mathematics is a language they can learn like any other.",
      },
      {
        q: "How are parents involved?",
        a: "Parents receive brief weekly notes summarising what was covered, where the student is progressing, and any recommended focus areas. You'll never wonder whether the sessions are working.",
      },
    ]}
    finalHeadlineLead="Real understanding.  Real"
    finalHeadlineItalic="results"
    finalBody="If your student is stuck, plateauing, or preparing for a major exam — the first step is a short discovery call to understand where they are and where they want to be."
    primaryCta={{
      label: "Explore Tutoring",
      kind: "mailto",
      subject: "Tutoring Inquiry — Mathematics & STEM",
    }}
    bookingSubject="Tutoring Booking — Mathematics & STEM"
  />
);

export default MathStemTutoring;
